class MadeLooseTheme {
  constructor() {
    this.menu = document.querySelector('[data-menu-drawer]');
    this.search = document.querySelector('[data-search-drawer]');
    this.overlay = document.querySelector('[data-drawer-overlay]');
    this.bindDrawers();
    this.bindHeader();
    this.bindReveal();
    this.bindEditorialFilms();
    this.bindHomeFold();
  }

  bindDrawers() {
    document.querySelectorAll('[data-menu-toggle]').forEach((button) => {
      button.addEventListener('click', () => this.toggleMenu());
    });
    document.querySelectorAll('[data-search-toggle]').forEach((button) => {
      button.addEventListener('click', () => this.toggleSearch());
    });
    this.overlay?.addEventListener('click', () => this.closeDrawers());
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeDrawers();
    });
  }

  toggleMenu() {
    const shouldOpen = !this.menu?.classList.contains('is-open');
    this.closeDrawers();
    if (!shouldOpen || !this.menu) return;
    this.menu.classList.add('is-open');
    this.menu.setAttribute('aria-hidden', 'false');
    this.overlay?.classList.add('is-open');
    document.body.classList.add('menu-open');
    document.querySelector('[data-menu-toggle]')?.setAttribute('aria-expanded', 'true');
    this.menu.querySelector('a, button')?.focus();
  }

  toggleSearch() {
    const shouldOpen = !this.search?.classList.contains('is-open');
    this.closeDrawers();
    if (!shouldOpen || !this.search) return;
    this.search.classList.add('is-open');
    this.search.setAttribute('aria-hidden', 'false');
    this.overlay?.classList.add('is-open');
    document.body.classList.add('search-open');
    document.querySelector('[data-search-toggle]')?.setAttribute('aria-expanded', 'true');
    window.setTimeout(() => this.search.querySelector('input')?.focus(), 120);
  }

  closeDrawers() {
    [this.menu, this.search].forEach((drawer) => {
      drawer?.classList.remove('is-open');
      drawer?.setAttribute('aria-hidden', 'true');
    });
    this.overlay?.classList.remove('is-open');
    document.body.classList.remove('menu-open', 'search-open');
    document.querySelectorAll('[data-menu-toggle], [data-search-toggle]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });
  }

  bindHeader() {
    const header = document.querySelector('[data-site-header]');
    if (!header) return;
    let previousY = window.scrollY;
    window.addEventListener('scroll', () => {
      const currentY = window.scrollY;
      header.classList.toggle('is-condensed', currentY > 16);
      header.classList.toggle('is-hidden', currentY > previousY && currentY > 180);
      previousY = currentY;
    }, { passive: true });
  }

  bindReveal() {
    const items = document.querySelectorAll('.reveal');
    if (!('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.08 });
    items.forEach((item) => observer.observe(item));
  }

  bindEditorialFilms() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.querySelectorAll('[data-editorial-film-toggle]').forEach((button) => {
      if (button.dataset.filmBound === 'true') return;
      const region = button.closest('.ml-home__hero, .ml-home__film, .ml-home__product-film');
      const film = region?.querySelector('[data-editorial-film]');
      const label = button.querySelector('[data-editorial-film-label]');
      if (!film || !label) return;

      const sync = () => {
        const paused = film.paused;
        button.setAttribute('aria-pressed', String(paused));
        label.textContent = paused ? 'Play film' : 'Pause film';
      };

      button.dataset.filmBound = 'true';
      button.addEventListener('click', () => {
        if (film.paused) film.play().catch(() => {});
        else film.pause();
        sync();
      });
      film.addEventListener('play', sync);
      film.addEventListener('pause', sync);
      if (reduceMotion) film.pause();
      sync();
    });
  }

  bindHomeFold() {
    const home = document.querySelector('.ml-home');
    const hero = home?.querySelector('.ml-home__hero');
    const facts = home?.querySelector('.ml-home__facts');
    if (!home || !hero || !facts || home.dataset.foldBound === 'true') return;

    const sync = () => {
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const heroTop = hero.getBoundingClientRect().top + window.scrollY;
      const factsHeight = facts.getBoundingClientRect().height;
      const availableHeight = Math.max(1, Math.floor(viewportHeight - heroTop - factsHeight));
      hero.style.setProperty('--home-hero-height', `${availableHeight}px`);
    };

    home.dataset.foldBound = 'true';
    window.requestAnimationFrame(sync);
    window.addEventListener('resize', sync, { passive: true });
    window.visualViewport?.addEventListener('resize', sync, { passive: true });
    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(sync);
      observer.observe(facts);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => new MadeLooseTheme());
document.addEventListener('shopify:section:load', () => new MadeLooseTheme());
